import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  Info,
  Target,
  Zap,
  Calendar
} from 'lucide-react';
import { useSessionContext } from '../../contexts/SessionContext';
import { toast } from 'react-hot-toast';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';

// Create a hook that safely uses CollegeContext
function useSafeCollegeContext() {
  try {
    const { useCollegeContext } = require('../../contexts/CollegeContext');
    return useCollegeContext();
  } catch (e) {
    return null;
  }
}

const SprintTimerOverlay = () => {
  const navigate = useNavigate();
  const {
    activeSession,
    sessionTimer,
    totalSessionTime,
    isPaused,
    shouldShowBreakReminder,
    breaksCount,
    toggleTaskCompletion,
    closeSession,
    togglePause,
    updateTimer,
    takeBreak,
    dismissBreakReminder
  } = useSessionContext();

  const collegeContext = useSafeCollegeContext();
  const colleges = collegeContext?.colleges || null;

  const [isCondensed, setIsCondensed] = useState(false);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [showTaskTip, setShowTaskTip] = useState(null);
  const [deadlineInfo, setDeadlineInfo] = useState(null);

  // Get deadline context
  useEffect(() => {
    if (colleges && colleges.length > 0) {
      const info = DeadlineIntelligenceService.getCurrentPhase(colleges);
      setDeadlineInfo(info);
    }
  }, [colleges]);

  // Timer logic
  useEffect(() => {
    if (!isPaused && sessionTimer > 0) {
      const timer = setInterval(() => {
        updateTimer(Math.max(0, sessionTimer - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isPaused, sessionTimer, updateTimer]);

  // Don't render if no active session
  if (!activeSession) return null;

  // Sprint mode detection
  const phase = deadlineInfo?.phase || 'NORMAL';
  const isSprintMode = phase === 'EMERGENCY_7' || phase === 'SPRINT_14' || phase === 'CRUNCH_30';
  const isEmergency = phase === 'EMERGENCY_7';

  // Task management
  const tasks = activeSession.tasks || [];
  const completedCount = tasks.filter(t => t.isCompleted).length;
  const totalCount = tasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const currentTask = tasks[currentTaskIndex] || null;

  // Time formatting
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    const percentRemaining = (sessionTimer / (activeSession?.totalTime || 1)) * 100;
    if (percentRemaining > 50) return 'text-white';
    if (percentRemaining > 25) return 'text-yellow-200';
    if (percentRemaining > 10) return 'text-orange-200';
    return 'text-red-200';
  };

  // Get gradient colors based on mode
  const getGradientColors = () => {
    if (isEmergency) return 'from-red-500 to-orange-500';
    if (isSprintMode) return 'from-orange-500 to-yellow-500';
    return 'from-teal-500 to-emerald-500';
  };

  // Task navigation
  const goToPrevTask = () => {
    setCurrentTaskIndex(prev => Math.max(0, prev - 1));
  };

  const goToNextTask = () => {
    setCurrentTaskIndex(prev => Math.min(tasks.length - 1, prev + 1));
  };

  // Task actions
  const handleTaskComplete = (task) => {
    toggleTaskCompletion(task.id);
    toast.success(`✅ ${task.title || task.friendly_title} completed`);

    // Auto-advance to next incomplete task
    const nextIncompleteIndex = tasks.findIndex((t, idx) =>
      idx > currentTaskIndex && !t.isCompleted
    );
    if (nextIncompleteIndex !== -1) {
      setCurrentTaskIndex(nextIncompleteIndex);
    }
  };

  const handleTaskClick = (task) => {
    if (task.module_link) {
      navigate(task.module_link);
    } else if (task.college_id) {
      navigate(`/college/colleges/${task.college_id}`);
    }
  };

  const handleClose = () => {
    closeSession();
    toast.success('Session saved. Great work! 🎉');
  };

  const toggleCondensed = () => {
    setIsCondensed(!isCondensed);
  };

  // Render condensed version - Responsive
  const renderCondensed = () => {
    return (
      <div className={`bg-gradient-to-r ${getGradientColors()} shadow-md`}>
        {/* Desktop Layout */}
        <div className="hidden sm:flex px-4 py-2 items-center justify-between">
          {/* Left: Mission title */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleCondensed}
              className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
              title="Expand timer"
            >
              <ChevronDown className="w-4 h-4 text-white" />
            </button>
            <div className="text-white">
              <span className="text-xs font-semibold">Today's Mission</span>
              {deadlineInfo && (
                <span className="text-[10px] text-white/80 ml-2">
                  {deadlineInfo.college} • {deadlineInfo.daysUntil} days
                </span>
              )}
            </div>
          </div>

          {/* Center: Progress */}
          <div className="flex items-center gap-2">
            <div className="text-white text-xs font-medium">
              {completedCount} of {totalCount} complete
            </div>
            <div className="w-24 h-1.5 bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Right: Timer & controls */}
          <div className="flex items-center gap-2">
            <span className={`font-mono text-sm font-semibold ${getTimerColor()}`}>
              {formatTime(sessionTimer)}
            </span>
            <button
              onClick={togglePause}
              className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? (
                <Play className="w-3.5 h-3.5 text-white" />
              ) : (
                <Pause className="w-3.5 h-3.5 text-white" />
              )}
            </button>
            <button
              onClick={handleClose}
              className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
              title="End session"
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="sm:hidden px-2 py-1.5 flex items-center justify-between gap-2">
          <button
            onClick={toggleCondensed}
            className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
            title="Expand timer"
          >
            <ChevronDown className="w-3.5 h-3.5 text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-white text-[10px] font-semibold truncate">
              Task {currentTaskIndex + 1}/{totalCount}
            </div>
          </div>
          <span className={`font-mono text-xs font-semibold ${getTimerColor()}`}>
            {formatTime(sessionTimer)}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={togglePause}
              className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? (
                <Play className="w-3 h-3 text-white" />
              ) : (
                <Pause className="w-3 h-3 text-white" />
              )}
            </button>
            <button
              onClick={handleClose}
              className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
              title="End session"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render expanded version
  const renderExpanded = () => (
    <div className={`bg-gradient-to-r ${getGradientColors()} shadow-lg`}>
      {/* Sprint Mode Banner */}
      {isSprintMode && deadlineInfo && (
        <div className="bg-black/20 px-4 py-2 flex items-center justify-between border-b border-white/20">
          <div className="flex items-center gap-2">
            {isEmergency ? (
              <Target className="w-4 h-4 text-white" />
            ) : (
              <Zap className="w-4 h-4 text-white" />
            )}
            <span className="text-xs font-semibold text-white uppercase tracking-wide">
              {isEmergency ? '🚨 Emergency Sprint' : '⚡ Sprint Mode'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-white/80" />
            <span className="text-xs text-white/90">
              {deadlineInfo.daysUntil} days → {deadlineInfo.college}
            </span>
          </div>
        </div>
      )}

      {/* Main Content - Responsive Compact Layout */}
      <div className="px-2 sm:px-4 py-2">
        {/* Desktop: Single Row Layout */}
        <div className="hidden lg:flex items-center justify-between gap-4 mb-2">
          {/* Left: Title and Progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h2 className="text-white font-semibold text-sm whitespace-nowrap">Today's Mission - Task {currentTaskIndex + 1} of {totalCount}</h2>
              {deadlineInfo && (
                <span className="text-white/80 text-xs whitespace-nowrap">
                  {deadlineInfo.college} • {deadlineInfo.daysUntil} days remaining
                </span>
              )}
            </div>
          </div>

          {/* Center: Large Timer */}
          <div className="text-center">
            <div className={`font-mono text-4xl font-bold tracking-tight ${getTimerColor()} ${
              sessionTimer <= 600 ? 'animate-pulse' : ''
            }`}>
              {formatTime(sessionTimer)}
            </div>
            <div className="text-[10px] text-white/60 font-medium uppercase tracking-wider">
              Time Remaining
            </div>
          </div>

          {/* Right: Progress and Controls */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-white font-semibold text-sm whitespace-nowrap">
                {completedCount} of {totalCount} tasks complete ({Math.round(progressPercentage)}%)
              </div>
              <div className="w-48 h-1.5 bg-white/30 rounded-full overflow-hidden mt-1">
                <div
                  className="h-full bg-white transition-all duration-500 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={togglePause}
                className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors flex items-center gap-1.5"
                title={isPaused ? 'Resume' : 'Pause'}
              >
                {isPaused ? <Play className="w-3.5 h-3.5 text-white" /> : <Pause className="w-3.5 h-3.5 text-white" />}
              </button>
              <button
                onClick={handleClose}
                className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors flex items-center gap-1.5"
                title="End Sprint"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
              <button
                onClick={toggleCondensed}
                className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
                title="Minimize"
              >
                <ChevronUp className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: Stacked Layout */}
        <div className="lg:hidden space-y-2">
          {/* Row 1: Title and Timer */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-semibold text-xs sm:text-sm truncate">
                Task {currentTaskIndex + 1} of {totalCount}
              </h2>
              {deadlineInfo && (
                <span className="text-white/80 text-[10px] sm:text-xs">
                  {deadlineInfo.college} • {deadlineInfo.daysUntil}d
                </span>
              )}
            </div>
            <div className="text-center">
              <div className={`font-mono text-2xl sm:text-3xl font-bold tracking-tight ${getTimerColor()} ${
                sessionTimer <= 600 ? 'animate-pulse' : ''
              }`}>
                {formatTime(sessionTimer)}
              </div>
            </div>
          </div>

          {/* Row 2: Progress and Controls */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <div className="text-white text-xs font-medium mb-1">
                {completedCount}/{totalCount} ({Math.round(progressPercentage)}%)
              </div>
              <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-500 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={togglePause}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title={isPaused ? 'Resume' : 'Pause'}
              >
                {isPaused ? <Play className="w-3.5 h-3.5 text-white" /> : <Pause className="w-3.5 h-3.5 text-white" />}
              </button>
              <button
                onClick={handleClose}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="End Sprint"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
              <button
                onClick={toggleCondensed}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Minimize"
              >
                <ChevronUp className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Task Carousel - Responsive */}
        {currentTask && (
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Previous Button */}
            <button
              onClick={goToPrevTask}
              disabled={currentTaskIndex === 0}
              className={`p-1 sm:p-1.5 rounded transition-colors flex-shrink-0 ${
                currentTaskIndex === 0
                  ? 'text-white/30 cursor-not-allowed'
                  : 'hover:bg-white/30 text-white'
              }`}
            >
              <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>

            {/* Current Task Card - Responsive */}
            <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 border border-white/20">
              {/* Desktop: Inline Layout */}
              <div className="hidden md:flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0">
                  <span className="px-1.5 py-0.5 bg-white/20 text-white text-[10px] font-bold rounded whitespace-nowrap">
                    STEP {currentTaskIndex + 1}
                  </span>
                  <h3 className={`text-white font-medium text-xs lg:text-sm flex-1 truncate ${
                    currentTask.isCompleted ? 'line-through opacity-60' : ''
                  }`}>
                    {currentTask.friendly_title || currentTask.title || currentTask.college_name}
                  </h3>
                  {currentTask.estimated_minutes && (
                    <div className="hidden lg:flex items-center gap-1 text-white/70 text-xs whitespace-nowrap">
                      <Clock className="w-3 h-3" />
                      <span>{currentTask.estimated_minutes} min</span>
                    </div>
                  )}
                  {(currentTask.task_tip || currentTask.metadata?.task_tip) && (
                    <div className="relative hidden lg:block">
                      <button
                        onClick={() => setShowTaskTip(showTaskTip === currentTask.id ? null : currentTask.id)}
                        className="text-white/80 hover:text-white transition-colors"
                        title="View task tip"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                      {showTaskTip === currentTask.id && (
                        <div className="absolute top-full mt-2 right-0 w-80 bg-white text-gray-800 text-xs p-2.5 rounded-lg shadow-xl z-10 border border-gray-200">
                          <p>{currentTask.task_tip || currentTask.metadata?.task_tip}</p>
                        </div>
                      )}
                    </div>
                  )}
                  {(currentTask.module_link || currentTask.college_id) && (
                    <button
                      onClick={() => handleTaskClick(currentTask)}
                      className="hidden lg:block text-white/80 text-xs underline hover:text-white transition-colors whitespace-nowrap"
                    >
                      Open →
                    </button>
                  )}
                </div>
                {!currentTask.isCompleted ? (
                  <button
                    onClick={() => handleTaskComplete(currentTask)}
                    className="px-2 lg:px-3 py-1 lg:py-1.5 bg-white text-teal-600 hover:bg-white/90 rounded-lg font-semibold text-xs lg:text-sm transition-colors whitespace-nowrap"
                  >
                    Complete
                  </button>
                ) : (
                  <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-white flex-shrink-0" />
                )}
              </div>

              {/* Mobile/Tablet: Stacked Layout */}
              <div className="md:hidden space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="px-1.5 py-0.5 bg-white/20 text-white text-[10px] font-bold rounded whitespace-nowrap">
                      STEP {currentTaskIndex + 1}
                    </span>
                    <h3 className={`text-white font-medium text-xs flex-1 truncate ${
                      currentTask.isCompleted ? 'line-through opacity-60' : ''
                    }`}>
                      {currentTask.friendly_title || currentTask.title || currentTask.college_name}
                    </h3>
                  </div>
                  {!currentTask.isCompleted ? (
                    <button
                      onClick={() => handleTaskComplete(currentTask)}
                      className="px-2 py-1 bg-white text-teal-600 hover:bg-white/90 rounded font-semibold text-[10px] transition-colors whitespace-nowrap"
                    >
                      Done
                    </button>
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                  )}
                </div>
                {currentTask.estimated_minutes && (
                  <div className="flex items-center gap-1 text-white/70 text-[10px]">
                    <Clock className="w-2.5 h-2.5" />
                    <span>{currentTask.estimated_minutes} min</span>
                  </div>
                )}
              </div>
            </div>

            {/* Next Button */}
            <button
              onClick={goToNextTask}
              disabled={currentTaskIndex === tasks.length - 1}
              className={`p-1 sm:p-1.5 rounded transition-colors flex-shrink-0 ${
                currentTaskIndex === tasks.length - 1
                  ? ' text-white/30 cursor-not-allowed'
                  : ' hover:bg-white/30 text-white'
              }`}
            >
              <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        )}

        {/* Task Dots Indicator - Smaller */}
        {tasks.length > 1 && (
          <div className="flex items-center justify-center gap-1 mt-1.5">
            {tasks.map((task, idx) => (
              <button
                key={task.id}
                onClick={() => setCurrentTaskIndex(idx)}
                className={`transition-all rounded-full ${
                  idx === currentTaskIndex
                    ? 'w-1.5 h-1.5 bg-white'
                    : task.isCompleted
                      ? 'w-1 h-1 bg-white/60'
                      : 'w-1 h-1 bg-white/30 hover:bg-white/50'
                }`}
                title={task.friendly_title || task.title}
              />
            ))}
          </div>
        )}
      </div>

      {/* Break Reminder - Compact Notification */}
      {shouldShowBreakReminder && (
        <div className="px-2 sm:px-4 pb-2">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg shadow-md p-2 sm:p-2.5 animate-pulse">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-base sm:text-lg animate-bounce">🌟</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-sm font-semibold">Time for a 5-min break!</div>
                  <div className="text-[10px] sm:text-xs text-white/90">You've earned it - 30 min completed</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={takeBreak}
                  className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white text-yellow-600 hover:bg-white/90 rounded font-semibold text-[10px] sm:text-xs transition-colors whitespace-nowrap"
                >
                  Take Break
                </button>
                <button
                  onClick={dismissBreakReminder}
                  className="p-1 sm:p-1.5 bg-white/20 hover:bg-white/30 rounded transition-colors"
                  title="Dismiss"
                >
                  <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render to portal
  return createPortal(
    <div className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300">
      {isCondensed ? renderCondensed() : renderExpanded()}
    </div>,
    document.body
  );
};

export default SprintTimerOverlay;
